'use client'

/**
 * FundingGauge Compound Component
 *
 * Category-aware funding progress visualization using compositional patterns.
 *
 * Usage:
 * ```tsx
 * <FundingGauge financials={award.financials} category={award.category}>
 *   <FundingGauge.Title />
 *   <FundingGauge.ProgressBar />
 *   <FundingGauge.Metrics />
 *   <FundingGauge.Note />
 * </FundingGauge>
 *
 * // Or use the pre-composed variant:
 * <FundingGaugeCard financials={award.financials} category={award.category} />
 * ```
 */
import React, { createContext, useContext } from 'react'

import { TrendingUp } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { Warning } from '@/components/callout/Warning'

import { CardV2 } from '../../../common/CardV2'
import type {
  ExtendedFinancialData,
  FundingGaugeContextValue,
  FundingGaugeState,
  FundingMetric,
  MetricColor,
} from './types'
import { useFundingGauge } from './useFundingGauge'

// ============================================================================
// Context
// ============================================================================

const FundingGaugeContext = createContext<FundingGaugeContextValue | null>(null)

function useFundingGaugeContext(): FundingGaugeContextValue {
  const ctx = useContext(FundingGaugeContext)
  if (!ctx) {
    throw new Error(
      'FundingGauge compound components must be used within a FundingGauge provider',
    )
  }
  return ctx
}

// ============================================================================
// Color Utilities
// ============================================================================

/**
 * Map semantic color to Tailwind text classes.
 */
function getTextColorClass(color: MetricColor): string {
  switch (color) {
    case 'accent':
      return 'text-accent-600 dark:text-accent-400'
    case 'success':
      return 'text-emerald-600 dark:text-emerald-400'
    case 'warning':
      return 'text-amber-600 dark:text-amber-400'
    case 'info':
      return 'text-blue-600 dark:text-blue-400'
    case 'muted':
      return 'text-primary-400 dark:text-primary-500'
    case 'primary':
    default:
      return 'text-primary-900 dark:text-primary-100'
  }
}

/**
 * Map semantic color to Tailwind background classes for progress bar.
 */
function getProgressBarColorClass(color: MetricColor): string {
  switch (color) {
    case 'accent':
      return 'bg-gradient-to-r from-accent-500 to-accent-400'
    case 'success':
      return 'bg-gradient-to-r from-emerald-500 to-emerald-400'
    case 'warning':
      return 'bg-gradient-to-r from-amber-500 to-amber-400'
    case 'info':
      return 'bg-gradient-to-r from-blue-500 to-blue-400'
    default:
      return 'bg-gradient-to-r from-primary-500 to-primary-400'
  }
}

// ============================================================================
// Root Component
// ============================================================================

export interface FundingGaugeProps {
  /** Raw financial data from the award */
  financials: ExtendedFinancialData
  /** Award category */
  category: string | null | undefined
  /** Award type code (for loan disambiguation) */
  type?: string | null
  /** IDV child order aggregation data */
  childOrderData?: {
    obligated: number
    outlayed: number
    count: number
  }
  /** Additional class names */
  className?: string
  /** Children (compound components) */
  children: React.ReactNode
}

/**
 * Root FundingGauge component.
 * Provides context to child compound components.
 */
function FundingGaugeRoot({
  financials,
  category,
  type,
  childOrderData,
  className,
  children,
}: FundingGaugeProps) {
  const state = useFundingGauge({
    financials,
    category,
    type,
    childOrderData,
  })

  return (
    <FundingGaugeContext.Provider value={{ state }}>
      <CardV2 variant="accent" className={className}>
        {children}
      </CardV2>
    </FundingGaugeContext.Provider>
  )
}

// ============================================================================
// Title Component
// ============================================================================

interface TitleProps {
  className?: string
}

function Title({ className }: TitleProps) {
  const { state } = useFundingGaugeContext()

  return (
    <CardV2.Header className={className}>
      <CardV2.HeaderIcon>
        <TrendingUp />
      </CardV2.HeaderIcon>
      <CardV2.HeaderTitle>{state.title}</CardV2.HeaderTitle>
    </CardV2.Header>
  )
}

// ============================================================================
// Content Component
// ============================================================================

interface ContentProps {
  children: React.ReactNode
  className?: string
}

function Content({ children, className }: ContentProps) {
  return <CardV2.Content className={className}>{children}</CardV2.Content>
}

// ============================================================================
// ProgressBar Component
// ============================================================================

interface ProgressBarProps {
  className?: string
}

function ProgressBar({ className }: ProgressBarProps) {
  const { state } = useFundingGaugeContext()

  if (!state.progress.showProgressBar) {
    return null
  }

  const { percentage, label, subtitle, color } = state.progress
  const barColorClass = getProgressBarColorClass(color)

  return (
    <div className={twMerge('mb-4', className)}>
      {/* Progress bar track */}
      <div className="mb-2 h-3 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-800">
        <div
          className={twMerge(
            'h-full rounded-full transition-all duration-500',
            barColorClass,
          )}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Labels row */}
      <div className="flex items-center justify-between text-xs text-primary-500 dark:text-primary-200">
        <span className="font-medium">{label}</span>
        {state.secondaryProgress && (
          <span>{state.secondaryProgress.label}</span>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="mt-1 text-xs text-primary-400 dark:text-primary-500">
          {subtitle}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Metrics Grid Component
// ============================================================================

interface MetricsProps {
  className?: string
  /** Override default column count */
  columns?: 2 | 3 | 4
}

function Metrics({ className, columns }: MetricsProps) {
  const { state } = useFundingGaugeContext()

  // Determine grid columns based on metric count or override
  const metricCount = state.metrics.length
  const gridCols = columns ?? (metricCount <= 2 ? 2 : metricCount <= 3 ? 3 : 4)

  const gridClass =
    gridCols === 2
      ? 'grid-cols-2'
      : gridCols === 3
        ? 'grid-cols-2 md:grid-cols-3'
        : 'grid-cols-2 md:grid-cols-4'

  return (
    <div className={twMerge('grid gap-4', gridClass, className)}>
      {state.metrics.map((metric) => (
        <MetricBlock key={metric.id} metric={metric} />
      ))}
    </div>
  )
}

// ============================================================================
// Individual Metric Block Component
// ============================================================================

interface MetricBlockProps {
  metric: FundingMetric
  className?: string
}

function MetricBlock({ metric, className }: MetricBlockProps) {
  const textColorClass = getTextColorClass(metric.color)

  return (
    <div className={className}>
      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
        {metric.label}
      </span>
      <div
        className={twMerge(
          'text-lg font-bold',
          metric.isPrimary ? 'text-xl' : '',
          textColorClass,
        )}
      >
        {metric.formattedValue}
      </div>
      {metric.description && (
        <div className="mt-0.5 text-xs text-primary-400 dark:text-primary-500">
          {metric.description}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Note Component
// ============================================================================

interface NoteProps {
  className?: string
}

function Note({ className }: NoteProps) {
  const { state } = useFundingGaugeContext()

  if (!state.note) {
    return null
  }

  return (
    <div className={twMerge('mt-4 w-full', className)}>
      <Warning className="w-full">{state.note}</Warning>
    </div>
  )
}

// ============================================================================
// Metrics Loading Skeleton Component
// ============================================================================

interface MetricsLoadingProps {
  className?: string
  /** Number of metric blocks to show (default: 4) */
  count?: number
}

function MetricsLoading({ className, count = 4 }: MetricsLoadingProps) {
  return (
    <div
      className={twMerge('grid grid-cols-2 gap-4 md:grid-cols-4', className)}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <span className="mb-1 block h-3 w-16 rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-7 w-20 rounded bg-primary-200 dark:bg-primary-700" />
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Pre-Composed Card Variant
// ============================================================================

export interface FundingGaugeCardProps {
  /** Raw financial data from the award */
  financials: ExtendedFinancialData
  /** Award category */
  category: string | null | undefined
  /** Award type code (for loan disambiguation) */
  type?: string | null
  /** IDV child order aggregation data */
  childOrderData?: {
    obligated: number
    outlayed: number
    count: number
  }
  /** Whether child order data is being loaded (for IDV awards) */
  isLoadingChildData?: boolean
  /** Additional class names */
  className?: string
}

/**
 * Pre-composed FundingGauge card with standard layout.
 * Use this for the common case; use compound components for custom layouts.
 */
export function FundingGaugeCard({
  financials,
  category,
  type,
  childOrderData,
  isLoadingChildData = false,
  className,
}: FundingGaugeCardProps) {
  return (
    <FundingGauge
      financials={financials}
      category={category}
      type={type}
      childOrderData={childOrderData}
      className={className}
    >
      <FundingGauge.Title />
      <FundingGauge.Content>
        <FundingGauge.ProgressBar />
        {isLoadingChildData ? (
          <FundingGauge.MetricsLoading />
        ) : (
          <FundingGauge.Metrics />
        )}
        <FundingGauge.Note />
      </FundingGauge.Content>
    </FundingGauge>
  )
}

// ============================================================================
// Compound Component Assembly
// ============================================================================

export const FundingGauge = Object.assign(FundingGaugeRoot, {
  Title,
  Content,
  ProgressBar,
  Metrics,
  MetricsLoading,
  Metric: MetricBlock,
  Note,
})

// ============================================================================
// Exports
// ============================================================================

export { useFundingGauge } from './useFundingGauge'
export type {
  ExtendedFinancialData,
  FundingGaugeCategory,
  FundingGaugeState,
  FundingMetric,
  MetricColor,
  ProgressConfig,
} from './types'
export {
  getCategoryFundingConfig,
  normalizeCategoryForFunding,
} from './configs'
